import { useEffect, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  fetchReportCampanas,
  fetchReportHorarios,
  fetchReportMotivosNoCierre,
  fetchReportProductos,
  fetchReportResumen,
  fetchReportSerie,
  reportCampanasQuery,
  reportProductosQuery,
  reportQueryFrom,
  reportSerieQuery,
} from '@/api/reports';
import { ZONA_HORARIA_POR_DEFECTO } from '@/lib/timezone';
import { queryKeys } from '@/lib/query-keys';
import type {
  AgruparCampanas,
  IntervaloReporte,
  OrdenProductos,
  ReportFiltros,
} from '@/types/reports';

/**
 * Datos de los reportes (módulo 10) del negocio activo.
 *
 * Los seis endpoints son de solo lectura y los ve cualquier miembro (`requireTenant`), así que acá
 * no hay mutaciones ni invalidaciones: las pantallas solo leen. (Cuando Campañas escribe el gasto,
 * el módulo de campañas invalida también `queryKeys.reports.all`, porque el CPA se calcula con ese
 * gasto: ver `use-campaigns.ts`.)
 *
 * Tres decisiones compartidas por todos los hooks:
 *  - los filtros + la zona horaria forman la query key: cambiar el rango, el canal o el `adId` es
 *    otra entrada de caché, y `keepPreviousData` mantiene los gráficos en pantalla mientras llega la
 *    nueva (sin parpadeo ni volver al esqueleto de carga);
 *  - `enabled: Boolean(tenantId)`: sin negocio activo no se dispara nada;
 *  - las pantallas no mandan nada cuando no hay filtros: el backend aplica su default (últimos 30
 *    días) y lo devuelve en `periodo`, que es lo que la pantalla muestra como «periodo efectivo».
 */

/** Lo que necesitan todos los hooks: negocio activo, filtros de la pantalla y zona del negocio. */
export interface ReportQueryParams {
  tenantId: string | null;
  filtros: Partial<ReportFiltros>;
  /** Zona del NEGOCIO con la que se convierten los días elegidos a instantes ISO. */
  zonaHoraria?: string;
}

/** `GET .../reports/resumen` — los KPIs del periodo (el primer bloque del panel). */
export function useReportResumen({ tenantId, filtros, zonaHoraria }: ReportQueryParams) {
  const query = reportQueryFrom(filtros, zonaHoraria ?? ZONA_HORARIA_POR_DEFECTO);

  return useQuery({
    queryKey: queryKeys.reports.resumen(tenantId ?? '', query),
    queryFn: () => fetchReportResumen(tenantId ?? '', query),
    enabled: Boolean(tenantId),
    placeholderData: keepPreviousData,
  });
}

/** `GET .../reports/serie` — la serie temporal del gráfico, por `dia` (default), `semana` o `mes`. */
export function useReportSerie({
  tenantId,
  filtros,
  zonaHoraria,
  intervalo,
}: ReportQueryParams & { intervalo: IntervaloReporte }) {
  const query = reportSerieQuery(filtros, zonaHoraria ?? ZONA_HORARIA_POR_DEFECTO, intervalo);

  return useQuery({
    queryKey: queryKeys.reports.serie(tenantId ?? '', query),
    queryFn: () => fetchReportSerie(tenantId ?? '', query),
    enabled: Boolean(tenantId),
    placeholderData: keepPreviousData,
  });
}

/** `GET .../reports/horarios` — el mapa de calor 7×24 (día × hora de la zona del negocio). */
export function useReportHorarios({ tenantId, filtros, zonaHoraria }: ReportQueryParams) {
  const query = reportQueryFrom(filtros, zonaHoraria ?? ZONA_HORARIA_POR_DEFECTO);

  return useQuery({
    queryKey: queryKeys.reports.horarios(tenantId ?? '', query),
    queryFn: () => fetchReportHorarios(tenantId ?? '', query),
    enabled: Boolean(tenantId),
    placeholderData: keepPreviousData,
  });
}

/** `GET .../reports/campanas` — rendimiento y CPA por anuncio (`anuncio`) o por campaña (`campania`). */
export function useReportCampanas({
  tenantId,
  filtros,
  zonaHoraria,
  agrupar,
}: ReportQueryParams & { agrupar: AgruparCampanas }) {
  const query = reportCampanasQuery(filtros, zonaHoraria ?? ZONA_HORARIA_POR_DEFECTO, agrupar);

  return useQuery({
    queryKey: queryKeys.reports.campanas(tenantId ?? '', query),
    queryFn: () => fetchReportCampanas(tenantId ?? '', query),
    enabled: Boolean(tenantId),
    placeholderData: keepPreviousData,
  });
}

/** `GET .../reports/productos` — ranking de productos vendidos (`limite` ≤ 100, `ordenar`). */
export function useReportProductos({
  tenantId,
  filtros,
  zonaHoraria,
  limite,
  ordenar,
}: ReportQueryParams & { limite: number; ordenar: OrdenProductos }) {
  const query = reportProductosQuery(filtros, zonaHoraria ?? ZONA_HORARIA_POR_DEFECTO, {
    limite,
    ordenar,
  });

  return useQuery({
    queryKey: queryKeys.reports.productos(tenantId ?? '', query),
    queryFn: () => fetchReportProductos(tenantId ?? '', query),
    enabled: Boolean(tenantId),
    placeholderData: keepPreviousData,
  });
}

/**
 * Zona horaria del NEGOCIO, la que usan los reportes para contar días y horas.
 *
 * El frontend no puede leer `REPORTS_TIMEZONE` (vive en el `.env` del backend), pero sí recibe la
 * zona REAL en cada respuesta (`periodo.zonaHoraria` y, en serie/horarios, `zonaHoraria`). Este par
 * de funciones resuelve el problema con dos pasos:
 *
 *  1. `useZonaHorariaReportes` devuelve la última zona CONOCIDA (guardada en `localStorage`) y, si
 *     nunca hubo respuesta, el default documentado (`America/Bogota`, ver `@/lib/timezone`);
 *  2. `recordarZonaHoraria` guarda la zona que trajo una respuesta y avisa a todas las pantallas
 *     montadas (evento en `window`), así el filtro de fechas y las etiquetas se corrigen solos.
 *
 * Con eso, si el negocio tiene otra zona configurada, la primera carga puede convertir el rango con
 * el default (y pedir una vez más los datos ya corregidos) y a partir de ahí todo —filtros, ejes y
 * textos— habla de la misma zona que el backend. Se evita así el pecado mayor: rotular «10 sept» un
 * día que el backend contó como el 11.
 */

const CLAVE_ZONA_GUARDADA = 'whatsapp-sales:reports-zona-horaria';
const EVENTO_ZONA = 'whatsapp-sales:reports-zona-horaria-cambiada';

/** Zona guardada (o `null` si todavía no hay ninguna, o si el navegador bloquea `localStorage`). */
function leerZonaGuardada(): string | null {
  try {
    return window.localStorage.getItem(CLAVE_ZONA_GUARDADA);
  } catch {
    return null;
  }
}

/** Zona vigente en la pestaña: la recordada o el default del backend. */
export function useZonaHorariaReportes(): string {
  const [zona, setZona] = useState(() => leerZonaGuardada() ?? ZONA_HORARIA_POR_DEFECTO);

  useEffect(() => {
    function alCambiar(evento: Event) {
      const zonaNueva = (evento as CustomEvent<string>).detail;
      if (zonaNueva) setZona(zonaNueva);
    }

    window.addEventListener(EVENTO_ZONA, alCambiar);
    return () => window.removeEventListener(EVENTO_ZONA, alCambiar);
  }, []);

  return zona;
}

/**
 * Guarda la zona que trajo una respuesta y avisa a las pantallas. No hace nada si coincide con la que
 * ya se conocía: es la llamada normal en cada carga, no un cambio real.
 */
export function recordarZonaHoraria(zona: string | null | undefined): void {
  if (!zona || zona === (leerZonaGuardada() ?? ZONA_HORARIA_POR_DEFECTO)) return;

  try {
    window.localStorage.setItem(CLAVE_ZONA_GUARDADA, zona);
  } catch {
    // Sin `localStorage` (modo privado) se sigue funcionando: solo se pierde el atajo entre visitas.
  }

  window.dispatchEvent(new CustomEvent<string>(EVENTO_ZONA, { detail: zona }));
}

/**
 * Reporta la zona horaria que trajo la última respuesta del backend.
 *
 * Cada respuesta de reportes dice en qué zona contó el backend (`zonaHoraria`, o
 * `periodo.zonaHoraria` en `resumen`). Las pantallas llaman a este hook con el primer valor que
 * tengan y `recordarZonaHoraria` se encarga del resto: si la zona coincide con la que ya se usaba no
 * pasa nada, y si no, se guarda y se corrige la pantalla (y los filtros) en el siguiente render.
 */
export function useZonaHorariaDeRespuesta(zona: string | null | undefined): void {
  useEffect(() => {
    recordarZonaHoraria(zona);
  }, [zona]);
}

/**
 * `GET .../reports/motivos-no-cierre` — el «por qué no cerró» de las conversaciones que nunca
 * llegaron a una orden. El endpoint no tiene parámetros extra: siempre cuenta todo el periodo.
 */
export function useReportMotivosNoCierre({ tenantId, filtros, zonaHoraria }: ReportQueryParams) {
  const query = reportQueryFrom(filtros, zonaHoraria ?? ZONA_HORARIA_POR_DEFECTO);

  return useQuery({
    queryKey: queryKeys.reports.motivosNoCierre(tenantId ?? '', query),
    queryFn: () => fetchReportMotivosNoCierre(tenantId ?? '', query),
    enabled: Boolean(tenantId),
    placeholderData: keepPreviousData,
  });
}
